import React, { useState, useMemo } from 'react';
import { User, UserStatus, Role, UserFilterState } from '@/types/domain/user';
import {
  ROLE_METADATA,
  ENTERPRISE_DEPARTMENTS,
  ENTERPRISE_PLANTS,
} from '@/config/user-management.config';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import {
  Eye,
  Pencil,
  Trash2,
  Lock,
  Search,
  FilterX,
  Shield,
  Loader2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Users,
  Briefcase,
  Landmark,
  UserCheck,
  Award,
  Crown,
  Cpu,
  FileCheck,
} from 'lucide-react';

interface UserTableProps {
  users: User[];
  totalCount: number;
  filterState: UserFilterState;
  onFilterChange: (newFilters: Partial<UserFilterState>) => void;
  onViewUser: (user: User) => void;
  onEditUser: (user: User) => void;
  onLockUser: (user: User) => void;
  onDeleteUser: (user: User) => void;
  isLoading?: boolean;
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
  sentinelRef?: (node: HTMLElement | null) => void;
}

// Polished, Human-Readable Enterprise Role Badges
const ROLE_BADGE_CONFIG: Record<
  Role,
  { label: string; icon: React.FC<{ className?: string }>; style: string }
> = {
  ADMIN: {
    label: 'Admin',
    icon: Shield,
    style: 'bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/25',
  },
  MANAGER: {
    label: 'Manager',
    icon: Users,
    style: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/25',
  },
  PROCUREMENT: {
    label: 'Procurement',
    icon: Briefcase,
    style: 'bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/25',
  },
  PROCUREMENT_MANAGER: {
    label: 'Proc. Lead',
    icon: Briefcase,
    style: 'bg-teal-500/10 text-teal-700 dark:text-teal-300 border-teal-500/25',
  },
  FINANCE: {
    label: 'Finance',
    icon: Landmark,
    style: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/25',
  },
  EMPLOYEE: {
    label: 'Employee',
    icon: UserCheck,
    style: 'bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/25',
  },
  NFA_LFA_APPROVER: {
    label: 'Approver',
    icon: Award,
    style: 'bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/25',
  },
  MANAGEMENT: {
    label: 'Executive',
    icon: Crown,
    style: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/25',
  },
  SAP_INTEGRATION: {
    label: 'SAP System',
    icon: Cpu,
    style: 'bg-zinc-500/10 text-zinc-700 dark:text-zinc-300 border-zinc-500/25',
  },
  AUDITOR: {
    label: 'Auditor',
    icon: FileCheck,
    style: 'bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/25',
  },
};

// Refined Enterprise Status Pills
const STATUS_BADGE_CONFIG: Record<
  UserStatus,
  { label: string; dot: string; style: string }
> = {
  ACTIVE: {
    label: 'Active',
    dot: 'bg-emerald-500',
    style: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/25',
  },
  INVITATION_PENDING: {
    label: 'Pending Invite',
    dot: 'bg-amber-500',
    style: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/25',
  },
  BLOCKED: {
    label: 'Quarantined',
    dot: 'bg-rose-500',
    style: 'bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/25',
  },
  INACTIVE: {
    label: 'Deactivated',
    dot: 'bg-slate-400',
    style: 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/25',
  },
  SUSPENDED: {
    label: 'Suspended',
    dot: 'bg-amber-500',
    style: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/25',
  },
  LOCKED: {
    label: 'Locked',
    dot: 'bg-red-500',
    style: 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/25',
  },
};

export const UserTable: React.FC<UserTableProps> = ({
  users,
  totalCount,
  filterState,
  onFilterChange,
  onViewUser,
  onEditUser,
  onLockUser,
  onDeleteUser,
  isLoading = false,
  isFetchingNextPage = false,
  hasNextPage = false,
  sentinelRef,
}) => {
  const [sortColumn, setSortColumn] = useState<keyof User | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Squeezed Status Filter Tabs
  const statusTabs: { id: UserStatus | 'ALL'; label: string }[] = [
    { id: 'ALL', label: 'All Users' },
    { id: 'ACTIVE', label: 'Active' },
    { id: 'INVITATION_PENDING', label: 'Pending Invite' },
    { id: 'BLOCKED', label: 'Quarantined' },
    { id: 'INACTIVE', label: 'Deactivated' },
    { id: 'LOCKED', label: 'Locked' },
  ];

  const hasActiveFilters = Boolean(
    filterState.search ||
      (filterState.status && filterState.status !== 'ALL') ||
      (filterState.role && filterState.role !== 'ALL') ||
      (filterState.department && filterState.department !== 'ALL') ||
      (filterState.plant && filterState.plant !== 'ALL')
  );

  const handleSort = (colKey: keyof User) => {
    if (sortColumn === colKey) {
      if (sortDirection === 'asc') setSortDirection('desc');
      else {
        setSortColumn(null);
        setSortDirection('asc');
      }
    } else {
      setSortColumn(colKey);
      setSortDirection('asc');
    }
  };

  // Sort data if sortColumn is active
  const sortedUsers = useMemo(() => {
    if (!sortColumn) return users;
    return [...users].sort((a, b) => {
      const aVal = String(a[sortColumn] ?? '').toLowerCase();
      const bVal = String(b[sortColumn] ?? '').toLowerCase();
      const comp = aVal < bVal ? -1 : 1;
      return sortDirection === 'asc' ? comp : -comp;
    });
  }, [users, sortColumn, sortDirection]);

  return (
    <Card className="border-border rounded-2xl shadow-xs overflow-hidden font-poppins w-full">
      {/* Card Header with Squeezed Status Tabs & Filters */}
      <CardHeader className="p-4 border-b border-border/60 bg-muted/20 space-y-3">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          {/* Squeezed Status Filter Tabs */}
          <div className="flex flex-wrap items-center gap-1.5">
            {statusTabs.map((tab) => {
              const isSelected = (filterState.status || 'ALL') === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => onFilterChange({ status: tab.id })}
                  className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer font-poppins ${
                    isSelected
                      ? 'bg-primary text-primary-foreground shadow-2xs font-bold'
                      : 'bg-card hover:bg-muted text-foreground-secondary hover:text-foreground border border-border/50'
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Search Bar */}
          <div className="relative w-full md:w-64 shrink-0">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-foreground-muted" />
            <input
              type="text"
              placeholder="Search user, ID, email..."
              value={filterState.search || ''}
              onChange={(e) => onFilterChange({ search: e.target.value })}
              className="w-full pl-9 pr-3 py-1.5 rounded-xl border border-input bg-card text-foreground text-xs focus:ring-1 focus:ring-primary focus:outline-none"
            />
          </div>
        </div>

        {/* Secondary Filter Dropdowns */}
        <div className="flex flex-wrap items-center justify-between gap-2.5 pt-2 border-t border-border/50">
          <div className="flex flex-wrap items-center gap-2">
            {/* Role Filter */}
            <select
              value={filterState.role || 'ALL'}
              onChange={(e) => onFilterChange({ role: e.target.value as any })}
              className="px-2.5 py-1.5 rounded-xl border border-border bg-card text-foreground text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="ALL">All Roles</option>
              {Object.keys(ROLE_METADATA).map((r) => (
                <option key={r} value={r}>
                  {ROLE_METADATA[r as Role].title}
                </option>
              ))}
            </select>

            {/* Department Filter */}
            <select
              value={filterState.department || 'ALL'}
              onChange={(e) => onFilterChange({ department: e.target.value })}
              className="px-2.5 py-1.5 rounded-xl border border-border bg-card text-foreground text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="ALL">All Departments</option>
              {ENTERPRISE_DEPARTMENTS.map((d) => (
                <option key={d.code} value={d.code}>
                  {d.name}
                </option>
              ))}
            </select>

            {/* Plant Filter */}
            <select
              value={filterState.plant || 'ALL'}
              onChange={(e) => onFilterChange({ plant: e.target.value })}
              className="px-2.5 py-1.5 rounded-xl border border-border bg-card text-foreground text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="ALL">All Operating Plants</option>
              {ENTERPRISE_PLANTS.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.name.split('—')[0]} ({p.code})
                </option>
              ))}
            </select>

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <button
                type="button"
                onClick={() =>
                  onFilterChange({
                    search: '',
                    status: 'ALL',
                    role: 'ALL',
                    department: 'ALL',
                    plant: 'ALL',
                  })
                }
                className="flex items-center gap-1 text-xs text-primary font-bold px-2.5 py-1 hover:bg-primary/10 rounded-xl transition-colors cursor-pointer"
              >
                <FilterX className="h-3.5 w-3.5" />
                <span>Reset</span>
              </button>
            )}
          </div>

          <div className="text-xs text-foreground-secondary font-medium">
            Showing <span className="font-bold text-foreground">{users.length}</span> of{' '}
            <span className="font-bold text-foreground">{totalCount}</span> identities
          </div>
        </div>
      </CardHeader>

      {/* Main Table Content - Zero Horizontal Scroll, 100% Width Fit, Centered Actions */}
      <CardContent className="p-0">
        <div className="max-h-[580px] overflow-y-auto overflow-x-hidden custom-scrollbar">
          <table className="w-full text-left border-collapse text-xs table-fixed font-poppins">
            <colgroup>
              <col className="w-[10%]" />
              <col className="w-[23%]" />
              <col className="w-[18%]" />
              <col className="w-[13%]" />
              <col className="w-[12%]" />
              <col className="w-[12%]" />
              <col className="w-[12%]" />
            </colgroup>
            <thead className="sticky top-0 z-10 bg-muted/90 backdrop-blur-xs border-b border-border/80 text-foreground-secondary font-bold text-[11px] uppercase tracking-wider">
              <tr>
                {/* Employee ID */}
                <th className="py-3 px-3.5">
                  <button
                    type="button"
                    onClick={() => handleSort('employeeId')}
                    className="flex items-center gap-1 hover:text-foreground font-extrabold uppercase transition-colors"
                  >
                    <span>Emp ID</span>
                    {sortColumn === 'employeeId' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3 text-primary" /> : <ArrowDown className="h-3 w-3 text-primary" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 text-foreground-muted" />
                    )}
                  </button>
                </th>

                {/* User Identity */}
                <th className="py-3 px-3">
                  <button
                    type="button"
                    onClick={() => handleSort('name')}
                    className="flex items-center gap-1 hover:text-foreground font-extrabold uppercase transition-colors"
                  >
                    <span>User & Designation</span>
                    {sortColumn === 'name' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3 text-primary" /> : <ArrowDown className="h-3 w-3 text-primary" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 text-foreground-muted" />
                    )}
                  </button>
                </th>

                {/* Corporate Email */}
                <th className="py-3 px-3">
                  <button
                    type="button"
                    onClick={() => handleSort('email')}
                    className="flex items-center gap-1 hover:text-foreground font-extrabold uppercase transition-colors"
                  >
                    <span>Corporate Email</span>
                    {sortColumn === 'email' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3 text-primary" /> : <ArrowDown className="h-3 w-3 text-primary" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 text-foreground-muted" />
                    )}
                  </button>
                </th>

                {/* Department & Plant */}
                <th className="py-3 px-3">Department</th>

                {/* Role */}
                <th className="py-3 px-3">Role</th>

                {/* Status */}
                <th className="py-3 px-3">Status</th>

                {/* Actions Header - Centered */}
                <th className="py-3 px-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {isLoading && users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-16 text-center text-foreground-muted">
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="h-5 w-5 text-primary animate-spin" />
                      <span className="font-medium text-xs">Loading enterprise identities...</span>
                    </div>
                  </td>
                </tr>
              ) : sortedUsers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-16 text-center text-foreground-muted">
                    No users matching current filter criteria.
                  </td>
                </tr>
              ) : (
                sortedUsers.map((user) => {
                  const roleConfig = ROLE_BADGE_CONFIG[user.role] || ROLE_BADGE_CONFIG.EMPLOYEE;
                  const statusConfig = STATUS_BADGE_CONFIG[user.status] || STATUS_BADGE_CONFIG.ACTIVE;
                  const RoleIcon = roleConfig.icon;

                  return (
                    <tr
                      key={user.id}
                      className="hover:bg-muted/40 transition-colors group"
                    >
                      {/* Employee ID */}
                      <td className="py-3 px-3.5 truncate">
                        <span className="font-mono font-bold text-foreground text-[11px] bg-muted/70 px-2 py-0.5 rounded-md border border-border/60 inline-block">
                          {user.employeeId}
                        </span>
                      </td>

                      {/* User & Designation */}
                      <td className="py-3 px-3 truncate">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="h-8 w-8 rounded-xl bg-gradient-to-tr from-primary/20 via-primary/10 to-transparent flex items-center justify-center text-primary font-black text-xs border border-primary/20 shrink-0">
                            {user.name.slice(0, 2).toUpperCase()}
                          </div>
                          <div className="min-w-0 truncate">
                            <span className="font-bold text-foreground group-hover:text-primary transition-colors block truncate text-xs">
                              {user.name}
                            </span>
                            <span className="text-[10px] text-foreground-muted block truncate">
                              {user.designation || ROLE_METADATA[user.role]?.title}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Corporate Email */}
                      <td className="py-3 px-3 truncate font-mono text-[11px] text-foreground-secondary" title={user.email}>
                        <span className="truncate block">{user.email}</span>
                      </td>

                      {/* Department & Plant */}
                      <td className="py-3 px-3 truncate">
                        <span className="font-medium text-foreground block truncate text-xs">
                          {user.departmentName}
                        </span>
                        <span className="text-[10px] text-foreground-muted font-mono block truncate">
                          {user.plantCode}
                        </span>
                      </td>

                      {/* Role Pill - Human readable, distinctive icon & color, never cut off */}
                      <td className="py-3 px-2.5 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold tracking-normal border shadow-2xs ${roleConfig.style}`}
                        >
                          <RoleIcon className="h-3 w-3 shrink-0" />
                          <span>{roleConfig.label}</span>
                        </span>
                      </td>

                      {/* Status Pill - Refined, subtle background with clear colored dot (zero ellipsis) */}
                      <td className="py-3 px-2.5 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium border shadow-2xs ${statusConfig.style}`}
                        >
                          <span className={`h-1.5 w-1.5 rounded-full ${statusConfig.dot} shrink-0`} />
                          <span>{statusConfig.label}</span>
                        </span>
                      </td>

                      {/* Actions: Eye, Edit, Lock, Delete - Perfectly Centered in Middle */}
                      <td className="py-3 px-3 text-center">
                        <div className="flex items-center justify-center gap-1.5 shrink-0">
                          {/* Eye: View Dossier */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onViewUser(user);
                            }}
                            title="View Identity Dossier"
                            className="h-7 w-7 rounded-lg hover:bg-primary/15 hover:text-primary active:scale-90 cursor-pointer text-foreground-secondary inline-flex items-center justify-center transition-all duration-200 group shadow-2xs hover:shadow-xs"
                          >
                            <Eye className="h-4 w-4 transition-transform duration-200 group-hover:scale-115" />
                          </button>

                          {/* Edit: Edit Profile */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditUser(user);
                            }}
                            title="Edit User Profile"
                            className="h-7 w-7 rounded-lg hover:bg-primary/10 cursor-pointer text-primary hover:text-primary-dark inline-flex items-center justify-center transition-colors"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>

                          {/* Lock: Lock/Unlock */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onLockUser(user);
                            }}
                            title={
                              user.status === 'LOCKED'
                                ? 'Unlock Account'
                                : user.status === 'BLOCKED'
                                ? 'Unblock Account'
                                : 'Quarantine / Lock User'
                            }
                            className="h-7 w-7 rounded-lg hover:bg-purple-500/15 cursor-pointer text-purple-600 dark:text-purple-400 inline-flex items-center justify-center transition-colors"
                          >
                            <Lock className="h-4 w-4" />
                          </button>

                          {/* Delete: Deactivate/Delete */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteUser(user);
                            }}
                            title="Deactivate / Delete User"
                            className="h-7 w-7 rounded-lg text-rose-500 hover:text-rose-600 hover:bg-rose-500/15 cursor-pointer inline-flex items-center justify-center transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}

              {/* Infinite Scroll Sentinel Row */}
              <tr ref={sentinelRef}>
                <td colSpan={7} className="py-2 text-center text-xs text-foreground-muted">
                  {isFetchingNextPage && (
                    <div className="flex items-center justify-center gap-2 py-3 text-primary font-semibold">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Loading more identities...</span>
                    </div>
                  )}
                  {!hasNextPage && users.length > 0 && (
                    <div className="text-[11px] text-foreground-muted py-2 font-medium">
                      ✓ All {totalCount} enterprise identities loaded
                    </div>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
